import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import RegisterModal from './components/RegisterModal';
import SearchResultTable, { SortKeyType } from './components/SearchResultTable';
import {
  GetModelNameList,
  SortOrder,
  RepairRequestInfo,
  PostRepairRequestQc,
} from '../../services/apiTypes';
import { RepairRequestListParam } from '../../stores/repairRequestList/api';
import { useDispatch } from 'react-redux';
import {
  createRepairRequest,
  fetchRepairRequest,
} from '../../stores/repairRequest/slice';
import { AppDispatch } from '../../stores/store';
import SearchResultForm from './components/SearchResultForm';
import { fetchRepairRequestList } from '../../stores/repairRequestList/slice';
import { INSPECTION_STATUSES } from '../../_constants/inspectionConst';
import {
  isGetModelNameList,
  isGetRepairRequest,
  isGetRepairRequestList,
} from '../../_utils/typeGuardUtils';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import AddButton from '../../_common/Button/AddButton';
import { getModelNameList } from '../../services/api/modelNameList';
import { INSPEX_PATHNAME, DEFAULT_ROWS_PER_PAGE } from '../../_constants/Const';
import { isRejected } from '@reduxjs/toolkit';
import { CustomError } from '../../services/CustomError';
import { API_CODE, CUSTOM_ERROR_CODE } from '../../_constants/apiConst';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const defaultParams: RepairRequestListParam = {
  rmaNo: '',
  serialNo: '',
  status: 2,
  rowFrom: 0,
  perPage: DEFAULT_ROWS_PER_PAGE,
};

/** 修理依頼検索画面 */
const RepairRequestSearch: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { error, showApiError, showTypeError, hideError } = useErrorModal();

  /** 起票のダイアログの開閉フラグ */
  const [isOpenModal, setIsOpenModal] = useState(false);
  /** 修理依頼の検索結果 */
  const [repairRequests, setRepairRequests] = useState<RepairRequestInfo[]>([]);
  /** 検索結果の総件数 */
  const [totalRows, setTotalRows] = useState<number>(0);
  /** 表示するページ */
  const [page, setPage] = useState(0);
  /** 検索フォームとソートの指定によるパラメータ */
  const [params, setParams] = useState<RepairRequestListParam>(defaultParams);
  /** ソート変更フラグ */
  const [isSortChanged, setIsSortChanged] = useState(false);
  /** 機種一覧 */
  const [modelList, setModelList] = useState<GetModelNameList>([]);

  useEffect(() => {
    if (isSortChanged) {
      fetchRepairRequests(params);
      setIsSortChanged(false);
      setPage(0);
    }
  }, [isSortChanged]);

  /** 検索結果一覧を取得 */
  const fetchRepairRequests = async (
    requestParams: RepairRequestListParam,
    isSearchButton = false,
  ) => {
    const response = await dispatch(fetchRepairRequestList(requestParams));

    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.REPAIR_REQUEST_LIST);
      return;
    } else if (!isGetRepairRequestList(response.payload)) {
      showTypeError(API_CODE.REPAIR_REQUEST_LIST);
      return;
    }

    const { repairRequestList } = response.payload;
    // 検索ボタン押下で一意に決まった場合は診断Topに遷移
    if (isSearchButton && repairRequestList.length === 1) {
      await prepareInspection(repairRequestList[0]);
      return;
    }
    setRepairRequests(repairRequestList);
    setTotalRows(response.payload.totalRows);
  };

  /** ステータスに応じて診断開始するための処理を実行 */
  const prepareInspection = async (request: RepairRequestInfo) => {
    // 修理依頼情報を再取得(一覧とBasicInfoで表示される検査者名のデータが異なるため)
    const response = await dispatch(
      fetchRepairRequest({ rmaNo: request.rmaNo }),
    );

    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.REPAIR_REQUEST);
      return;
    } else if (!isGetRepairRequest(response.payload)) {
      showTypeError(API_CODE.REPAIR_REQUEST);
      return;
    }

    // ステータスに応じて遷移先を分岐
    switch (request.status) {
      // 受入診断開始前
      case INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK:
        navigate(INSPEX_PATHNAME.SERIAL_CHECK);
        break;
      // 受入診断進行中
      case INSPECTION_STATUSES.INSPECTION_IN_PROGRESS:
        navigate(INSPEX_PATHNAME.INSPECTION_TOP);
        break;
      // 受入診断結果確認
      case INSPECTION_STATUSES.INSPECTION_INPUT_RESULT:
        navigate(INSPEX_PATHNAME.INSPECTION_INPUT_RESULT);
        break;
      // 確定診断
      case INSPECTION_STATUSES.INSPECTION_CONFIRM:
        navigate(INSPEX_PATHNAME.CONFIRMED_INSPECTION);
        break;
      // パーツ選択
      case INSPECTION_STATUSES.INSPECTION_PARTS_SELECT:
        navigate(INSPEX_PATHNAME.PARTS_SELECTION);
        break;
      // 受入診断データ未連携
      case INSPECTION_STATUSES.INSPECTION_NOT_LINKED:
        navigate(INSPEX_PATHNAME.REPORT);
        break;
      // No Fault Found
      case INSPECTION_STATUSES.NO_FAULT_FOUND:
        navigate(INSPEX_PATHNAME.REPORT);
        break;
      // 出荷検査待ち(受入診断完了)
      case INSPECTION_STATUSES.INSPECTION_COMPLETE: {
        // QCデータを新規で作成
        const qcRequest: PostRepairRequestQc = {
          rmaNo: response.payload.rmaNo,
          status: response.payload.status,
          serialNo: response.payload.serialNo,
          model: response.payload.model,
        };
        const qcResponse = await dispatch(createRepairRequest(qcRequest));
        if (isRejected(qcResponse)) {
          if (qcResponse.payload instanceof CustomError) {
            if (qcResponse.payload.code === CUSTOM_ERROR_CODE.NO_TEMPLATE) {
              qcResponse.payload.data = [t('LBL_STATUS_QC')];
              showApiError(qcResponse.payload, API_CODE.REPAIR_REQUEST);
              return;
            }
          }
          showApiError(qcResponse.payload, API_CODE.REPAIR_REQUEST);
          return;
        }
        navigate(INSPEX_PATHNAME.SERIAL_CHECK);
        break;
      }
      // 出荷検査中
      case INSPECTION_STATUSES.QC_IN_PROGRESS: {
        navigate(INSPEX_PATHNAME.INSPECTION_TOP);
        break;
      }
      // 出荷検査確定診断
      case INSPECTION_STATUSES.QC_CONFIRM: {
        navigate(INSPEX_PATHNAME.CONFIRMED_QC);
        break;
      }
      // 出荷検査後の修理中
      case INSPECTION_STATUSES.QC_REPAIRING:
        navigate(INSPEX_PATHNAME.SERIAL_CHECK);
        break;
      // 出荷検査データ未連携
      case INSPECTION_STATUSES.QC_NOT_LINKED:
        navigate(INSPEX_PATHNAME.REPORT);
        break;
      // 出荷検査完了
      case INSPECTION_STATUSES.QC_COMPLETE:
        navigate(INSPEX_PATHNAME.REPORT);
        break;
      default:
        break;
    }
  };

  const handleAddDialogOpen = async () => {
    if (modelList.length > 0) {
      setIsOpenModal(true);
      return;
    }
    try {
      const response = await getModelNameList();
      if (!isGetModelNameList(response)) {
        showTypeError(API_CODE.MODEL_NAME_LIST);
        return;
      }
      setModelList(response);
      setIsOpenModal(true);
    } catch (error) {
      showApiError(error, API_CODE.MODEL_NAME_LIST);
    }
  };

  const handleAddDialogClose = (isClose: boolean) => {
    if (isClose === true) {
      setIsOpenModal(false);
    }
  };

  /** 検索ボタン押下 */
  const onClickSearch = () => {
    fetchRepairRequests(params, true);
    setPage(0);
  };

  /** クリアボタン押下 */
  const onClickClear = () => {
    // 検索パラメータのみクリア
    const { sortKey, order, rowFrom, perPage } = params;
    setParams({ ...defaultParams, sortKey, order, rowFrom, perPage });
  };

  /** ソート実施時 */
  const handleSortChange = (sortKey: SortKeyType, order: SortOrder) => {
    setParams((prev) => ({
      ...prev,
      sortKey,
      order,
    }));
    setIsSortChanged(true);
  };

  return (
    <Box mt={8}>
      <SearchResultForm
        params={params}
        updateParams={setParams}
        onClickSearch={onClickSearch}
        onClickClear={onClickClear}
      />
      <SearchResultTable
        params={params}
        updateParams={setParams}
        page={page}
        updatePage={setPage}
        repairRequests={repairRequests}
        totalRows={totalRows}
        onSortChange={handleSortChange}
        prepareInspection={prepareInspection}
        fetchRepairRequests={fetchRepairRequests}
      />
      <AddButton onClick={handleAddDialogOpen} />
      <RegisterModal
        isOpen={isOpenModal}
        handleClose={handleAddDialogClose}
        modelList={modelList}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default RepairRequestSearch;
